import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { cn } from "@postman/core/cn";
import {
  roundsQuery,
  useAddQuestion,
  useCreateRound,
  useDeleteRound,
  useUpdateQuestion,
  useUpdateRound,
  type BuilderQuestion,
  type BuilderRound,
} from "@/lib/coord";
import {
  Button,
  Callout,
  Card,
  Check,
  Empty,
  Field,
  Input,
  Label,
  Pill,
  Textarea,
} from "@/components/ui";
import { DateField, Select } from "@/components/pick";
import { AttachmentManager } from "@/components/attachments";
import { Markdown } from "@/components/markdown";

export const Route = createFileRoute("/_auth/coord/$vertical/rounds")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(roundsQuery(params.vertical)),
  component: Builder,
});

const KINDS = [
  { value: "task", label: "Task — they submit something" },
  { value: "interview", label: "Interview" },
  { value: "group", label: "Group activity" },
  { value: "other", label: "Something else" },
] as const;

const QUESTION_KINDS = [
  { value: "short", label: "Short text" },
  { value: "long", label: "Paragraph" },
  { value: "url", label: "Link" },
  { value: "select", label: "Pick one" },
  { value: "multiselect", label: "Pick several" },
  { value: "boolean", label: "Yes / no" },
] as const;

/**
 * The form builder.
 *
 * Two constraints are visible in the UI because they are real constraints in
 * the API, not preferences: rounds are appended rather than reordered (moving
 * one rewrites where every live applicant is standing), and a round with any
 * submissions or decisions cannot be deleted. Both are enforced server-side;
 * what this file does is explain them before someone tries.
 */
function Builder() {
  const { vertical } = Route.useParams();
  const { data } = useSuspenseQuery(roundsQuery(vertical));
  const entry = data.rounds.find((r) => r.kind === "application") ?? null;

  return (
    <div className="flex flex-col gap-10">
      <p className="max-w-[62ch] text-[15px] leading-relaxed text-ink-2">
        The application form is round zero — the same thing as every round after
        it, which is why deadlines, forms and decisions work identically all the
        way down. Rounds are added at the end and never reordered: moving one
        would silently change which stage every live applicant is standing in.
      </p>

      {!entry ? (
        <Card className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <h2 className="display text-[22px]">Nobody can apply yet</h2>
            <p className="max-w-[56ch] text-[15px] leading-relaxed text-ink-2">
              This vertical has no application form, so it shows as closed on the
              picker. Make one and it opens immediately.
            </p>
          </div>
          <NewRound vertical={vertical} kind="application" label="Create the application form" />
        </Card>
      ) : null}

      <div className="flex flex-col gap-8">
        {data.rounds.map((round) => (
          <RoundEditor key={round.id} vertical={vertical} round={round} />
        ))}
      </div>

      {entry ? (
        <Card className="flex flex-col gap-5">
          <h2 className="mono text-ink-3">Add a round</h2>
          <NewRound vertical={vertical} label="Add it" />
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Same instant, or not — never a string comparison.
 *
 * `DateField` hands back `toISOString()` and the server hands back whatever
 * Postgres serialised, so two spellings of one moment are normal and a `!==`
 * on them leaves the Save button lit with nothing to save.
 */
function sameInstant(a: string | null, b: string | null): boolean {
  if (!a || !b) return a === b;
  return new Date(a).getTime() === new Date(b).getTime();
}

function NewRound({
  vertical,
  kind: fixedKind,
  label,
}: {
  vertical: string;
  kind?: "application";
  label: string;
}) {
  const create = useCreateRound(vertical);
  const [name, setName] = useState(fixedKind ? "Application" : "");
  const [kind, setKind] = useState<(typeof KINDS)[number]["value"]>("task");
  const [closesAt, setClosesAt] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[14rem] flex-1 flex-col gap-2">
          <Label htmlFor="new-name">Name</Label>
          <Input
            id="new-name"
            value={name}
            maxLength={120}
            placeholder="Take-home task"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        {!fixedKind ? (
          <div className="flex min-w-[14rem] flex-col gap-2">
            <Label htmlFor="new-kind">Kind</Label>
            <Select id="new-kind" value={kind} options={KINDS} onChange={setKind} />
          </div>
        ) : null}
        <div className="flex flex-col gap-2">
          <Label htmlFor="new-closes">Deadline</Label>
          <DateField id="new-closes" value={closesAt} onChange={setClosesAt} />
        </div>
        <Button
          disabled={!name.trim() || create.isPending}
          onClick={() =>
            create.mutate(
              {
                kind: fixedKind ?? kind,
                name: name.trim(),
                closesAt,
              },
              { onSuccess: () => { setName(fixedKind ? "Application" : ""); setClosesAt(""); } },
            )
          }
        >
          {create.isPending ? "Adding" : label}
        </Button>
      </div>
      {create.error ? <Callout tone="error">{(create.error as Error).message}</Callout> : null}
    </div>
  );
}

function RoundEditor({ vertical, round }: { vertical: string; round: BuilderRound }) {
  const update = useUpdateRound(vertical);
  const remove = useDeleteRound(vertical);

  const [name, setName] = useState(round.name);
  const [instructions, setInstructions] = useState(round.instructions ?? "");
  const [closesAt, setClosesAt] = useState<string | null>(round.closesAt);
  const [saved, setSaved] = useState(false);

  const used = round.submissionCount > 0 || round.decisionCount > 0;
  const dirty =
    name !== round.name ||
    instructions !== (round.instructions ?? "") ||
    !sameInstant(closesAt, round.closesAt);

  return (
    <Card className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Pill tone={round.kind === "application" ? "live" : "quiet"}>
            {round.position === 0 ? "Round 0" : `Round ${round.position}`}
          </Pill>
          <span className="mono text-ink-3">{round.kind}</span>
        </div>
        <p className="mono text-ink-3">
          {round.submissionCount} submitted · {round.decisionCount} decided
        </p>
      </div>

      <div className="flex flex-col gap-6">
        <Field label="Name">
          <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field
          label="Instructions"
          help="Markdown: **bold**, `code`, - lists, [links](https://…). Shown to an applicant only once they reach this round."
        >
          <Textarea
            rows={8}
            value={instructions}
            maxLength={4000}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </Field>

        {/* The preview is under the box rather than beside it on purpose: at
            this column width a split view halves both halves, and what a
            coordinator is checking is whether their list came out as a list. */}
        {instructions.trim() ? (
          <div className="flex flex-col gap-3">
            <p className="mono text-ink-3">How they will read it</p>
            <div className="border border-line bg-paper-3 px-5 py-4">
              <Markdown source={instructions} className="max-w-[62ch]" />
            </div>
          </div>
        ) : null}
        <Field
          label="Deadline"
          help="Late submissions still land — they arrive flagged, and you decide what that is worth."
        >
          <DateField value={closesAt} onChange={setClosesAt} />
        </Field>

        <div className="flex flex-wrap items-center gap-4">
          <Button
            disabled={!dirty || update.isPending}
            onClick={() =>
              update.mutate(
                {
                  roundId: round.id,
                  name: name.trim(),
                  instructions,
                  closesAt,
                },
                { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 1500); } },
              )
            }
          >
            {update.isPending ? "Saving" : saved ? "Saved" : "Save round"}
          </Button>

          <Button
            variant="danger"
            className="h-9 px-3"
            disabled={used || remove.isPending}
            title={used ? "People have already worked on this round." : undefined}
            onClick={() => remove.mutate(round.id)}
          >
            Delete
          </Button>
          {used ? (
            <span className="mono text-ink-3">
              Locked — people have worked on it
            </span>
          ) : null}
        </div>
        {update.error ? <Callout tone="error">{(update.error as Error).message}</Callout> : null}
        {remove.error ? <Callout tone="error">{(remove.error as Error).message}</Callout> : null}
      </div>

      <div className="flex flex-col gap-5 border-t border-line pt-7">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="mono text-ink-3">Files</h3>
          <span className="mono text-ink-3">{round.attachments.length} attached</span>
        </div>
        {round.attachments.length === 0 ? (
          <Empty>
            Nothing attached. A brief, a starter repo or a dataset goes here
            rather than in a link that expires.
          </Empty>
        ) : null}
        <AttachmentManager
          vertical={vertical}
          roundId={round.id}
          items={round.attachments}
        />
      </div>

      <Questions vertical={vertical} round={round} />
    </Card>
  );
}

function Questions({ vertical, round }: { vertical: string; round: BuilderRound }) {
  const questions = round.form?.questions ?? [];
  const live = questions.filter((q) => !q.archived);
  const archived = questions.filter((q) => q.archived);

  return (
    <div className="flex flex-col gap-5 border-t border-line pt-7">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="mono text-ink-3">Questions</h3>
        <span className="mono text-ink-3">{live.length} live</span>
      </div>

      {live.length === 0 ? (
        <Empty>
          No questions. A round with none is legitimate — an interview collects
          nothing from the applicant — but an application form with none is
          probably not what you meant.
        </Empty>
      ) : (
        <ul className="flex flex-col gap-3">
          {live.map((q, i) => (
            <QuestionRow
              key={q.id}
              vertical={vertical}
              roundId={round.id}
              question={q}
              neighbours={live}
              index={i}
            />
          ))}
        </ul>
      )}

      {archived.length > 0 ? (
        <details className="border border-line px-4 py-3">
          <summary className="mono cursor-pointer text-ink-3">
            {archived.length} archived
          </summary>
          <ul className="mt-4 flex flex-col gap-3">
            {archived.map((q, i) => (
              <QuestionRow
                key={q.id}
                vertical={vertical}
                roundId={round.id}
                question={q}
                neighbours={archived}
                index={i}
              />
            ))}
          </ul>
        </details>
      ) : null}

      <NewQuestion vertical={vertical} roundId={round.id} />
    </div>
  );
}

function QuestionRow({
  vertical,
  roundId,
  question,
  neighbours,
  index,
}: {
  vertical: string;
  roundId: string;
  question: BuilderQuestion;
  neighbours: BuilderQuestion[];
  index: number;
}) {
  const update = useUpdateQuestion(vertical);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(question.label);
  const [help, setHelp] = useState(question.help ?? "");
  const [required, setRequired] = useState(question.required);

  function swap(delta: number) {
    const other = neighbours[index + delta];
    if (!other) return;
    // Two patches rather than one reorder route: question positions are a
    // presentation detail inside one form, and nothing derives from them.
    update.mutate({ roundId, questionId: question.id, position: other.position });
    update.mutate({ roundId, questionId: other.id, position: question.position });
  }

  return (
    <li className={cn("border border-line", question.archived && "opacity-60")}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          className="mono shrink-0 text-ink-3 hover:text-ink disabled:opacity-30"
          disabled={index === 0}
          aria-label="Move up"
          onClick={() => swap(-1)}
        >
          ↑
        </button>
        <button
          className="mono shrink-0 text-ink-3 hover:text-ink disabled:opacity-30"
          disabled={index === neighbours.length - 1}
          aria-label="Move down"
          onClick={() => swap(1)}
        >
          ↓
        </button>

        <button
          className="min-w-0 flex-1 text-left text-[15px] hover:text-[color:var(--accent-text)]"
          onClick={() => setOpen((o) => !o)}
        >
          {question.label}
        </button>

        <span className="mono shrink-0 text-ink-3">{question.kind}</span>
        {question.required ? <Pill tone="live">Required</Pill> : null}
      </div>

      {open ? (
        <div className="flex flex-col gap-5 border-t border-line px-4 py-5">
          <Field label="Label">
            <Input value={label} maxLength={300} onChange={(e) => setLabel(e.target.value)} />
          </Field>
          <Field label="Help text">
            <Input value={help} maxLength={500} onChange={(e) => setHelp(e.target.value)} />
          </Field>
          <Check
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            label="Required"
          />
          {question.options ? (
            <p className="mono text-ink-3">
              Options: {question.options.join(" · ")} — options can be added or
              renamed, never removed. Archive the question instead.
            </p>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button
              className="h-9 px-3"
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { roundId, questionId: question.id, label: label.trim(), help, required },
                  { onSuccess: () => setOpen(false) },
                )
              }
            >
              Save
            </Button>
            <Button
              variant="ghost"
              className="h-9 px-3"
              onClick={() =>
                update.mutate({
                  roundId,
                  questionId: question.id,
                  archived: !question.archived,
                })
              }
            >
              {question.archived ? "Restore" : "Archive"}
            </Button>
          </div>
          <p className="text-[13px] leading-relaxed text-ink-3">
            Archived, never deleted: answers are keyed by this question, so
            removing it would erase what everyone already wrote for it.
          </p>
          {update.error ? (
            <Callout tone="error">{(update.error as Error).message}</Callout>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function NewQuestion({ vertical, roundId }: { vertical: string; roundId: string }) {
  const add = useAddQuestion(vertical);
  const [kind, setKind] = useState<(typeof QUESTION_KINDS)[number]["value"]>("long");
  const [label, setLabel] = useState("");
  const [help, setHelp] = useState("");
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState("");

  const needsOptions = kind === "select" || kind === "multiselect";

  return (
    <div className="flex flex-col gap-5 border border-dashed border-line px-4 py-5">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[16rem] flex-1 flex-col gap-2">
          <Label htmlFor={`q-label-${roundId}`}>Question</Label>
          <Input
            id={`q-label-${roundId}`}
            value={label}
            maxLength={300}
            placeholder="What have you built that you are proud of?"
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div className="flex min-w-[12rem] flex-col gap-2">
          <Label htmlFor={`q-kind-${roundId}`}>Answer type</Label>
          <Select
            id={`q-kind-${roundId}`}
            value={kind}
            options={QUESTION_KINDS}
            onChange={setKind}
          />
        </div>
      </div>

      <Field label="Help text" help="Optional. Sits under the field.">
        <Input value={help} maxLength={500} onChange={(e) => setHelp(e.target.value)} />
      </Field>

      {needsOptions ? (
        <Field label="Options" help="One per line. At least two.">
          <Textarea rows={3} value={options} onChange={(e) => setOptions(e.target.value)} />
        </Field>
      ) : null}

      <Check
        checked={required}
        onChange={(e) => setRequired(e.target.checked)}
        label="Required"
      />

      <div>
        <Button
          disabled={!label.trim() || add.isPending}
          onClick={() =>
            add.mutate(
              {
                roundId,
                kind,
                label: label.trim(),
                help: help.trim() || undefined,
                required,
                options: needsOptions
                  ? options.split("\n").map((o) => o.trim()).filter(Boolean)
                  : undefined,
              },
              {
                onSuccess: () => {
                  setLabel("");
                  setHelp("");
                  setOptions("");
                  setRequired(false);
                },
              },
            )
          }
        >
          {add.isPending ? "Adding" : "Add question"}
        </Button>
      </div>
      {add.error ? <Callout tone="error">{(add.error as Error).message}</Callout> : null}
    </div>
  );
}
